import { create } from 'zustand';
import { usePropertyStore } from './propertyStore';
import { useModuleStore } from './moduleStore';
import { queryClient } from '../lib/queryClient';
import { useExperienceStore } from './experienceStore';
import { secureStorage } from '../lib/secureStorage';
import type {
  User,
  AuthResponse,
  CorePortalAuthResponse,
  LinkedTenant,
} from '../types/auth';

/**
 * Auth store — ports AuthService state from the Angular member portal.
 *
 * Storage strategy:
 *   - Access token   : in-memory (Zustand) only. Lost on app kill, refreshed on demand.
 *   - Refresh token  : secure storage (Keychain/Keystore on native; localStorage on web).
 *   - User profile   : localStorage (non-sensitive snapshot for offline first paint).
 *   - Tenant + URL   : localStorage (paas_tenant_id, paas_regional_api_url) — keys preserved
 *                      verbatim from Angular for cross-build interop.
 *   - SSO session    : secure storage (paas_sso_session_token).
 *   - Linked tenants : localStorage (paas_sso_tenants) — list, not credentials.
 *   - Active portal  : localStorage (paas_active_portal_domain) — last domain used after login
 *                      so dev refreshes don't re-resolve the SSO hub.
 *
 * Storage keys mirror Angular constants exactly.
 */

const ACCESS_TOKEN_KEY = 'paas_access_token';
const REFRESH_TOKEN_KEY = 'paas_refresh_token';
const USER_KEY = 'paas_user';
const SSO_SESSION_KEY = 'paas_sso_session_token';
const SSO_TENANTS_KEY = 'paas_sso_tenants';
const ACTIVE_PORTAL_DOMAIN_KEY = 'paas_active_portal_domain';
const ACTIVE_COMMUNITY_TENANT_KEY = 'paas_active_community_tenant_id';
const TENANT_ID_KEY = 'paas_tenant_id';
const REGIONAL_API_URL_KEY = 'paas_regional_api_url';
const REMEMBER_SESSION_KEY = 'paas_remember_session';

interface AuthPersistenceOptions {
  remember?: boolean;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  tenantId: string | null;
  regionalApiUrl: string;
  ssoSessionToken: string | null;
  linkedTenants: LinkedTenant[];
  rememberSession: boolean;

  isAuthenticated: () => boolean;

  /** Persist tokens + user after a successful auth response. */
  setAuth: (response: AuthResponse | CorePortalAuthResponse, options?: AuthPersistenceOptions) => Promise<void>;

  setRegionalApiUrl: (url: string) => void;
  setTenantId: (tenantId: string | null) => void;
  setActivePortalDomain: (domain: string | null | undefined) => void;
  setSso: (response: CorePortalAuthResponse, options?: AuthPersistenceOptions) => Promise<void>;
  setLinkedTenants: (tenants: LinkedTenant[]) => void;
  setRememberSession: (remember: boolean) => void;

  /** Clear everything — use on full sign-out. */
  signOut: () => Promise<void>;

  /** Read-only helpers for axios interceptors and guards. */
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  getTenantId: () => string | null;
  getRegionalApiUrl: () => string;

  /** Hydrate from storage on cold start. Idempotent. */
  hydrate: () => Promise<void>;
}

function readJSON<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function syncLicenseModules(user: User | null): void {
  useModuleStore.getState().syncLicensedModules(user?.license?.enabled_modules ?? []);
}

/** Map tenant API login user (`user_id`) into the canonical `User` shape. */
export function normalizeAuthUser(raw: User | null | undefined): User | null {
  if (!raw) return null;
  const userId = raw.userId ?? raw.user_id ?? '';
  const tenantId = raw.tenantId ?? raw.tenant_id ?? '';
  return {
    ...raw,
    userId,
    tenantId,
    user_id: raw.user_id ?? userId,
    tenant_id: raw.tenant_id ?? tenantId,
  };
}

/** Authenticated tenant user id (JWT `user_id` / store `userId`). */
export function getSessionUserId(): string | undefined {
  const user = normalizeAuthUser(useAuthStore.getState().user);
  if (user?.userId) return user.userId;

  const token = useAuthStore.getState().getAccessToken();
  if (!token) return undefined;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { user_id?: string };
    return payload.user_id;
  } catch {
    return undefined;
  }
}

function persistRememberSession(remember: boolean): void {
  try {
    localStorage.setItem(REMEMBER_SESSION_KEY, JSON.stringify(remember));
  } catch {
    /* noop */
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  refreshToken: null,
  user: null,
  tenantId: null,
  regionalApiUrl: '',
  ssoSessionToken: null,
  linkedTenants: [],
  rememberSession: true,

  isAuthenticated() {
    return !!(get().accessToken || get().refreshToken);
  },

  async setAuth(response, options) {
    if (!response.success) return;
    const { accessToken, refreshToken, user: rawUser } = response;
    const user = normalizeAuthUser(rawUser);
    const rememberSession = options?.remember ?? get().rememberSession;
    set({ accessToken, refreshToken, user, rememberSession });
    syncLicenseModules(user);
    persistRememberSession(rememberSession);

    if (rememberSession) {
      try {
        // Mirror Angular: access token also kept in localStorage so legacy
        // helpers / interceptors still pick it up if invoked from contexts that
        // can't reach the in-memory store. Refresh token never goes here.
        localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      } catch (error) {
        console.warn('[auth] persist user failed:', error);
      }
      if (refreshToken) {
        await secureStorage.set(REFRESH_TOKEN_KEY, refreshToken);
      }
      return;
    }

    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch { /* noop */ }
    await secureStorage.remove(REFRESH_TOKEN_KEY);
  },

  setRegionalApiUrl(url) {
    set({ regionalApiUrl: url });
    if (url) {
      try { localStorage.setItem(REGIONAL_API_URL_KEY, url); } catch { /* noop */ }
    }
  },

  setTenantId(tenantId) {
    set({ tenantId });
    try {
      if (tenantId) localStorage.setItem(TENANT_ID_KEY, tenantId);
      else localStorage.removeItem(TENANT_ID_KEY);
    } catch { /* noop */ }
  },

  setActivePortalDomain(domain) {
    const d = domain?.trim();
    try {
      if (d) localStorage.setItem(ACTIVE_PORTAL_DOMAIN_KEY, d);
      else localStorage.removeItem(ACTIVE_PORTAL_DOMAIN_KEY);
    } catch { /* noop */ }
  },

  async setSso(response, options) {
    const rememberSession = options?.remember ?? get().rememberSession;
    set({ rememberSession });
    persistRememberSession(rememberSession);

    if (response.regional_api_base_url) {
      get().setRegionalApiUrl(response.regional_api_base_url);
    }
    if (response.tenant_id) {
      get().setTenantId(response.tenant_id);
    }
    if (response.domain) {
      get().setActivePortalDomain(response.domain);
    }
    try {
      if (response.tenant_id) {
        localStorage.setItem(ACTIVE_COMMUNITY_TENANT_KEY, response.tenant_id);
      }
      if (!response.tenant_id && response.domain) {
        localStorage.removeItem(ACTIVE_COMMUNITY_TENANT_KEY);
      }
    } catch {
      /* noop */
    }

    const sessionToken = response.sso?.session_token;
    if (sessionToken) {
      set({ ssoSessionToken: sessionToken });
      if (rememberSession) {
        await secureStorage.set(SSO_SESSION_KEY, sessionToken);
      } else {
        await secureStorage.remove(SSO_SESSION_KEY);
      }
    }

    if (Array.isArray(response.sso?.tenants)) {
      const tenants = response.sso.tenants;
      set({ linkedTenants: tenants });
      try {
        if (rememberSession) localStorage.setItem(SSO_TENANTS_KEY, JSON.stringify(tenants));
        else localStorage.removeItem(SSO_TENANTS_KEY);
      } catch { /* noop */ }
    }
  },

  setRememberSession(rememberSession) {
    set({ rememberSession });
    persistRememberSession(rememberSession);
  },

  setLinkedTenants(tenants) {
    set({ linkedTenants: tenants });
    try { localStorage.setItem(SSO_TENANTS_KEY, JSON.stringify(tenants)); } catch { /* noop */ }
  },

  /**
   * Sign out — mirrors Angular `AuthService.logout()`: clear credentials only.
   * Keeps `paas_tenant_id`, `paas_regional_api_url`, and branding (same hostname).
   */
  async signOut() {
    const { tenantId, regionalApiUrl } = get();
    set({
      accessToken: null,
      refreshToken: null,
      user: null,
      tenantId,
      regionalApiUrl,
      ssoSessionToken: null,
      linkedTenants: [],
    });
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(SSO_TENANTS_KEY);
      localStorage.removeItem(ACTIVE_PORTAL_DOMAIN_KEY);
      localStorage.removeItem(ACTIVE_COMMUNITY_TENANT_KEY);
      // Intentionally keep TENANT_ID_KEY + REGIONAL_API_URL_KEY for login/public APIs.
    } catch { /* noop */ }
    await secureStorage.remove(REFRESH_TOKEN_KEY);
    await secureStorage.remove(SSO_SESSION_KEY);
    usePropertyStore.getState().clear();
    useModuleStore.getState().setConfig(null);
    useExperienceStore.getState().setExperience(null);
    syncLicenseModules(null);
    // Drop the previous member's cached queries (profile, properties, utilities,
    // etc.) now the session has ended — so re-logging in as a different user
    // can't surface stale data. Safe here: logged-out routing shows the login
    // screen, which has no member-data queries mounted.
    queryClient.clear();
  },

  getAccessToken() { return get().accessToken; },
  getRefreshToken() { return get().refreshToken; },
  getTenantId() { return get().tenantId; },
  getRegionalApiUrl() { return get().regionalApiUrl; },

  async hydrate() {
    if (typeof window === 'undefined') return;

    const accessToken = (() => {
      try { return localStorage.getItem(ACCESS_TOKEN_KEY); } catch { return null; }
    })();
    const userRaw = (() => {
      try { return localStorage.getItem(USER_KEY); } catch { return null; }
    })();
    const user = normalizeAuthUser(readJSON<User>(userRaw));
    const tenantId = (() => {
      try { return localStorage.getItem(TENANT_ID_KEY); } catch { return null; }
    })();
    const regionalApiUrl = (() => {
      try { return localStorage.getItem(REGIONAL_API_URL_KEY) ?? ''; } catch { return ''; }
    })();
    const linkedTenantsRaw = (() => {
      try { return localStorage.getItem(SSO_TENANTS_KEY); } catch { return null; }
    })();
    const linkedTenants = readJSON<LinkedTenant[]>(linkedTenantsRaw) ?? [];
    const rememberSession = (() => {
      try {
        const raw = localStorage.getItem(REMEMBER_SESSION_KEY);
        return raw == null ? true : raw !== 'false';
      } catch {
        return true;
      }
    })();

    if (!rememberSession) {
      set({
        accessToken: null,
        refreshToken: null,
        user: null,
        tenantId,
        regionalApiUrl,
        ssoSessionToken: null,
        linkedTenants: [],
        rememberSession,
      });
      syncLicenseModules(null);
      return;
    }

    const refreshToken = await secureStorage.get(REFRESH_TOKEN_KEY);
    const ssoSessionToken = await secureStorage.get(SSO_SESSION_KEY);

    set({
      accessToken,
      refreshToken,
      user,
      tenantId,
      regionalApiUrl,
      ssoSessionToken,
      linkedTenants,
      rememberSession,
    });
    syncLicenseModules(user);
  },
}));

export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: ACCESS_TOKEN_KEY,
  REFRESH_TOKEN: REFRESH_TOKEN_KEY,
  USER: USER_KEY,
  SSO_SESSION: SSO_SESSION_KEY,
  SSO_TENANTS: SSO_TENANTS_KEY,
  ACTIVE_PORTAL_DOMAIN: ACTIVE_PORTAL_DOMAIN_KEY,
  ACTIVE_COMMUNITY_TENANT: ACTIVE_COMMUNITY_TENANT_KEY,
  TENANT_ID: TENANT_ID_KEY,
  REGIONAL_API_URL: REGIONAL_API_URL_KEY,
  REMEMBER_SESSION: REMEMBER_SESSION_KEY,
};
